import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, User } from "lucide-react";
import { z } from "zod";

const profileSchema = z.object({
  full_name: z.string()
    .min(1, "Full name is required")
    .max(100, "Full name must be less than 100 characters")
    .trim(),
  phone_number: z.string()
    .refine((val) => !val || /^\+[1-9]\d{1,14}$/.test(val), {
      message: "Phone number must be in E.164 format (e.g., +1234567890) or empty"
    }),
  avatar_url: z.string()
    .refine((val) => !val || z.string().url().safeParse(val).success, {
      message: "Avatar URL must be a valid URL or empty"
    })
});

export const ProfileSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    avatar_url: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      setProfile({
        full_name: data.full_name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
        avatar_url: data.avatar_url || '',
      });
    } catch (error: any) {
      toast({
        title: "Error loading profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    
    try {
      // Validate input data
      const validationResult = profileSchema.safeParse({
        full_name: profile.full_name,
        phone_number: profile.phone_number,
        avatar_url: profile.avatar_url,
      });

      if (!validationResult.success) {
        const fieldErrors: Record<string, string> = {};
        validationResult.error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(fieldErrors);
        toast({
          title: "Validation Error",
          description: "Please fix the errors in the form",
          variant: "destructive",
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: validationResult.data.full_name,
          phone_number: validationResult.data.phone_number || null,
          avatar_url: validationResult.data.avatar_url || null,
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile has been saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error saving profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <User className="h-5 w-5" />
          <div>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="full-name">Full Name</Label>
          <Input
            id="full-name"
            value={profile.full_name}
            onChange={(e) => {
              setProfile({ ...profile, full_name: e.target.value });
              setErrors({ ...errors, full_name: '' });
            }}
            placeholder="Enter your full name"
            className={errors.full_name ? "border-destructive" : ""}
          />
          {errors.full_name && (
            <p className="text-sm text-destructive">{errors.full_name}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={profile.email}
            disabled
            className="bg-muted"
          />
          <p className="text-sm text-muted-foreground">Email cannot be changed</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            value={profile.phone_number}
            onChange={(e) => {
              setProfile({ ...profile, phone_number: e.target.value });
              setErrors({ ...errors, phone_number: '' });
            }}
            placeholder="+1234567890"
            className={errors.phone_number ? "border-destructive" : ""}
          />
          {errors.phone_number && (
            <p className="text-sm text-destructive">{errors.phone_number}</p>
          )}
          <p className="text-sm text-muted-foreground">Must be in E.164 format (e.g., +1234567890)</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="avatar">Avatar URL (optional)</Label>
          <Input
            id="avatar"
            type="url"
            value={profile.avatar_url}
            onChange={(e) => {
              setProfile({ ...profile, avatar_url: e.target.value });
              setErrors({ ...errors, avatar_url: '' });
            }}
            placeholder="https://example.com/avatar.jpg"
            className={errors.avatar_url ? "border-destructive" : ""}
          />
          {errors.avatar_url && (
            <p className="text-sm text-destructive">{errors.avatar_url}</p>
          )}
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Profile
        </Button>
      </CardContent>
    </Card>
  );
};
